import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu } from "lucide-react";
import { useState, useCallback } from "react";
import { Link } from "react-router-dom";

// Page import functions for preloading
const pageImports: Record<string, () => Promise<any>> = {
  "/": () => import("@/pages/Index"),
  "/extras-carte-funciara": () => import("@/pages/OrderExtrasCF"),
  "/blog": () => import("@/pages/Blog"),
  "/contact": () => import("@/pages/Contact"),
  "/hub/auth": () => import("@/pages/hub/HubAuth"),
};

// Track preloaded pages to avoid duplicate preloads
const preloadedPages = new Set<string>();

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);

  const navLinks = [
    { href: "/", label: "Acasă" },
    { href: "/extras-carte-funciara", label: "Extras CF Online" },
    { href: "/blog", label: "Blog" },
    { href: "/contact", label: "Contact" },
  ];

  // Preload page on hover
  const handlePreload = useCallback((href: string) => {
    if (preloadedPages.has(href)) return;
    
    const importFn = pageImports[href];
    if (importFn) {
      preloadedPages.add(href);
      importFn().catch(() => {
        // Remove from set if preload fails so it can be retried
        preloadedPages.delete(href);
      });
    }
  }, []);

  return (
    <nav className="bg-card border-b border-border sticky top-[52px] z-40">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link 
              to="/" 
              className="text-2xl font-bold text-primary hover:opacity-80 transition-opacity"
              onMouseEnter={() => handlePreload("/")}
            >
              DocuRapid
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="text-sm font-medium text-foreground hover:text-primary transition-colors"
                onMouseEnter={() => handlePreload(link.href)}
                onTouchStart={() => handlePreload(link.href)}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-2">
            <Button variant="outline" size="sm" asChild>
              <Link to="/contact">Verifică Status</Link>
            </Button>
            <Button variant="default" size="sm" asChild onMouseEnter={() => handlePreload("/hub/auth")}>
              <Link to="/hub/auth">Contul Meu</Link>
            </Button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm">
                  <Menu className="h-5 w-5" />
                  <span className="sr-only">Deschide meniul</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                <nav className="flex flex-col gap-4 mt-8">
                  {navLinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={() => setIsOpen(false)}
                      onTouchStart={() => handlePreload(link.href)}
                      className="text-lg font-medium text-foreground hover:text-primary transition-colors py-2 border-b border-border/50 hover:border-primary/50"
                    >
                      {link.label}
                    </Link>
                  ))}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-4 w-full"
                    asChild
                  >
                    <Link to="/contact" onClick={() => setIsOpen(false)}>
                      Verifică Status
                    </Link>
                  </Button>
                  <Button 
                    variant="default" 
                    size="sm" 
                    className="mt-2 w-full"
                    asChild
                  >
                    <Link to="/hub/auth" onClick={() => setIsOpen(false)}>
                      Contul Meu
                    </Link>
                  </Button>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
